import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  GetMyPreferencesDocument,
  UpdatePreferencesDocument,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * The summary sections the viewer keeps folded — a personal reading
 * preference, keyed by section id (the summary widget type), that applies on
 * every project the viewer opens and follows the account across devices.
 * Which sections exist and their order are the project type's; folding is
 * the one thing that stays the reader's.
 *
 * Two layers, the `useNavPreferences` shape: the server list is the truth
 * (the network fetch is owned by `workspace-data-provider`; cache-first here
 * avoids a duplicate request), and a local override makes a press land
 * instantly, cleared when the latest mutation settles either way.
 */
export function useCollapsedSummarySections() {
  const { data } = useQuery(GetMyPreferencesDocument, {
    fetchPolicy: "cache-first",
  });
  const [updatePreferences] = useMutation(UpdatePreferencesDocument);
  const [local, setLocal] = useState<string[] | null>(null);
  const latestRequest = useRef(0);

  const serverCollapsed = data?.myPreferences?.collapsedSummarySections;
  // Memoized so the `[]` fallback is one identity, not a fresh array per
  // render feeding the set and the setter below.
  const collapsed = useMemo<string[]>(
    () => local ?? serverCollapsed ?? [],
    [local, serverCollapsed],
  );
  const collapsedSet = useMemo(() => new Set(collapsed), [collapsed]);

  const setCollapsed = useCallback(
    (sectionType: string, isCollapsed: boolean) => {
      const has = collapsed.includes(sectionType);
      if (has === isCollapsed) return;
      const next = isCollapsed
        ? [...collapsed, sectionType]
        : collapsed.filter((id) => id !== sectionType);
      setLocal(next);
      const request = ++latestRequest.current;
      updatePreferences({
        variables: { input: { collapsedSummarySections: next } },
        onCompleted: () => {
          if (request === latestRequest.current) setLocal(null);
        },
        onError: (err) => {
          if (request === latestRequest.current) setLocal(null);
          toastMutationError(err, "Failed to save fold");
        },
      });
    },
    [collapsed, updatePreferences],
  );

  return { collapsedSet, setCollapsed };
}
